/**
 * ServiceConfig Component
 *
 * Dynamic configuration UI that adapts based on selected service type.
 * - Virtual Staging: 4 category tabs (Modern, Minimalist, Classic, Custom with 12 styles)
 * - Item Removal: Text description input
 * - Description: Format selector (Caption, Description, MLS Remarks)
 * - Day-to-Dusk: Confirmation only
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Tailwind CSS core utilities only
 * - Greens/blues color palette
 * - User-friendly validation
 */

import React, { useState } from 'react';
import { ServiceType, DescriptionFormat, EnhancementMode } from '@/shared/types';
import { Input, Card } from '@/components/ui';
import { InpaintingCanvas } from './InpaintingCanvas';

export interface ServiceConfigData {
  // For Virtual Staging
  stagingStyle?: string;

  // For Item Removal
  removalPrompt?: string;
  maskCoordinates?: { x: number; y: number; width: number; height: number };
  maskImage?: string; // Base64 encoded mask image

  // For Description
  descriptionFormat?: DescriptionFormat;
  propertyDetails?: string;

  // For Photo Enhancement
  enhancementMode?: EnhancementMode;
}

export interface ServiceConfigProps {
  serviceType: ServiceType;
  config: ServiceConfigData;
  onChange: (config: ServiceConfigData) => void;
  errors?: Record<string, string>;
  imageUrl?: string;
}

/**
 * ServiceConfig Component
 *
 * @param serviceType - Selected service type
 * @param config - Current configuration data
 * @param onChange - Callback when configuration changes
 * @param errors - Validation errors
 */
export function ServiceConfig({
  serviceType,
  config,
  onChange,
  errors = {},
  imageUrl,
}: ServiceConfigProps): React.JSX.Element {
  const [activeCategory, setActiveCategory] = useState<string>('modern');
  const [isSelectionEnabled, setIsSelectionEnabled] = useState<boolean>(false);

  // Staging style categories and options
  const stagingCategories = {
    modern: ['Contemporary', 'Scandinavian', 'Industrial', 'Mid-Century Modern'],
    minimalist: ['Japanese Zen', 'Nordic', 'Clean Lines', 'Monochrome'],
    classic: ['Traditional', 'French Country', 'Colonial', 'Victorian'],
    custom: [
      'Bohemian',
      'Coastal',
      'Farmhouse',
      'Rustic',
      'Art Deco',
      'Mediterranean',
      'Transitional',
      'Eclectic',
      'Luxury',
      'Urban Loft',
      'Tropical',
      'Vintage',
    ],
  };

  // Render Virtual Staging configuration
  const renderStagingConfig = (): React.JSX.Element => {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Staging Style</h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose a furniture style that best suits the property
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex space-x-2 border-b border-gray-200">
          {Object.keys(stagingCategories).map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 font-medium text-sm transition-all duration-200 ${activeCategory === category
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Style Options Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {stagingCategories[activeCategory as keyof typeof stagingCategories].map((style) => {
            const isSelected = config.stagingStyle === style;

            return (
              <button
                key={style}
                onClick={() => onChange({ ...config, stagingStyle: style })}
                className={`p-4 rounded-lg border-2 text-sm font-medium transition-all duration-200 hover:scale-105 ${isSelected
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
              >
                {style}
              </button>
            );
          })}
        </div>

        {config.stagingStyle && (
          <div className="bg-green-50 border border-green-500 text-green-700 px-4 py-3 rounded-lg">
            <p className="font-semibold">Selected: {config.stagingStyle}</p>
          </div>
        )}
      </div>
    );
  };

  // Render Item Removal configuration
  const renderRemovalConfig = (): React.JSX.Element => {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Area to Remove</h3>
          <p className="text-sm text-gray-600 mb-4">
            Draw a box around the item(s) you want to remove.
          </p>
        </div>

        {imageUrl && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enable-selection"
                checked={isSelectionEnabled}
                onChange={(e) => setIsSelectionEnabled(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="enable-selection" className="text-sm font-medium text-gray-700 select-none">
                Enable Selection Tool (Disables scrolling on image)
              </label>
            </div>

            <div className={`mb-6 border-2 rounded-lg overflow-hidden transition-colors duration-200 ${isSelectionEnabled ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'}`}>
              <InpaintingCanvas
                imageUrl={imageUrl}
                enabled={isSelectionEnabled}
                onChange={(maskImage) => {
                  if (maskImage) {
                    onChange({ ...config, maskImage });
                  } else {
                    const newConfig = { ...config };
                    delete newConfig.maskImage;
                    onChange(newConfig);
                  }
                }}
              />
            </div>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Describe What to Remove</h3>
          <p className="text-sm text-gray-600 mb-4">
            Be specific about the location for best results (e.g., "remove the red car in the driveway")
          </p>
        </div>

        <Input
          label="Removal Description"
          id="removal-prompt"
          name="removal-prompt"
          type="textarea"
          value={config.removalPrompt || ''}
          onChange={(value) => onChange({ ...config, removalPrompt: value as string })}
          placeholder="Example: remove the red car in the driveway"
          error={errors.removalPrompt}
          required
        />

        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
          <p className="font-semibold mb-1">💡 Pro Tip:</p>
          <p>
            Include color and location for better accuracy. Our AI uses context-aware replacement to
            seamlessly fill the background.
          </p>
        </div>
      </div>
    );
  };

  // Render Description configuration
  const renderDescriptionConfig = (): React.JSX.Element => {
    const formats = [
      {
        value: DescriptionFormat.CAPTION,
        name: 'Caption',
        description: 'Brief social media caption (~150 characters)',
      },
      {
        value: DescriptionFormat.DESCRIPTION,
        name: 'Description',
        description: 'Marketing description (50-200 words)',
      },
      {
        value: DescriptionFormat.MLS_REMARKS,
        name: 'MLS Remarks',
        description: 'Comprehensive MLS-compliant remarks (max 999 characters)',
      },
    ];

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Description Format</h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose the format that best suits your needs
          </p>
        </div>

        {/* Format Selection */}
        <div className="space-y-3">
          {formats.map((format) => {
            const isSelected = config.descriptionFormat === format.value;

            return (
              <Card
                key={format.value}
                className={`cursor-pointer transition-all duration-200 hover:scale-102 hover:shadow-lg ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                onClick={() => onChange({ ...config, descriptionFormat: format.value })}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                      }`}
                  >
                    {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{format.name}</h4>
                    <p className="text-sm text-gray-600">{format.description}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Property Details Input (Optional) */}
        {config.descriptionFormat && (
          <div className="space-y-4">
            <Input
              label="Property Details (Optional)"
              id="property-details"
              name="property-details"
              type="textarea"
              value={config.propertyDetails || ''}
              onChange={(value) => onChange({ ...config, propertyDetails: value as string })}
              placeholder="Add any specific details you want highlighted (bedrooms, features, location, etc.)"
              error={errors.propertyDetails}
            />

            {config.descriptionFormat === DescriptionFormat.MLS_REMARKS && (
              <p className="text-sm text-gray-600">
                Character limit: 999 characters
                {config.propertyDetails && (
                  <span
                    className={`ml-2 font-semibold ${config.propertyDetails.length > 999 ? 'text-red-600' : 'text-green-600'
                      }`}
                  >
                    ({config.propertyDetails.length}/999)
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render Photo Enhancement configuration
  const renderEnhancementConfig = (): React.JSX.Element => {
    const modes = [
      {
        value: EnhancementMode.DUSK,
        title: 'Day to Dusk',
        icon: '🌙',
        description: 'Turn daytime exteriors into stunning twilight photos.',
        badge: 'Best for Exteriors',
      },
      {
        value: EnhancementMode.GENERAL,
        title: 'Lighting & Color',
        icon: '💡',
        description: 'Brighten interiors, correct white balance, and apply HDR style.',
        badge: 'Best for Interiors',
      },
    ];

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Enhancement Mode</h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose how you want to enhance your photo
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {modes.map((mode) => {
            const isSelected = config.enhancementMode === mode.value;

            return (
              <Card
                key={mode.value}
                className={`cursor-pointer transition-all duration-200 hover:scale-102 hover:shadow-lg relative overflow-hidden ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                onClick={() => onChange({ ...config, enhancementMode: mode.value })}
              >
                {/* Badge */}
                <div className="absolute top-0 right-0 bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded-bl-lg">
                  {mode.badge}
                </div>

                <div className="p-4 flex flex-col items-center text-center space-y-3">
                  <span className="text-4xl" role="img" aria-label={mode.title}>
                    {mode.icon}
                  </span>
                  <div>
                    <h4 className="font-bold text-gray-900">{mode.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{mode.description}</p>
                  </div>
                  {isSelected && (
                    <div className="text-blue-600 font-semibold text-sm flex items-center">
                      <span className="mr-1">✓</span> Selected
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {errors.enhancementMode && (
          <p className="text-red-500 text-sm mt-2">{errors.enhancementMode}</p>
        )}

        {config.enhancementMode && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
            <p>
              {config.enhancementMode === EnhancementMode.DUSK
                ? 'Your image will be transformed with a beautiful sunset/twilight effect.'
                : 'Your image will be professionally enhanced with better lighting and colors.'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render appropriate configuration based on service type
  return (
    <div className="space-y-6">
      {serviceType === ServiceType.STAGING && renderStagingConfig()}
      {serviceType === ServiceType.ITEM_REMOVAL && renderRemovalConfig()}
      {serviceType === ServiceType.DESCRIPTION && renderDescriptionConfig()}
      {serviceType === ServiceType.DAY_TO_DUSK && renderEnhancementConfig()}
    </div>
  );
}
